/**
 * Browser-safe crypto helpers (no Node 'crypto' dependency)
 * Uses @noble/hashes — audited, zero-dep, works in browser + worker.
 */

import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils';

/** SHA-256 of hex string → hex digest */
export function sha256Hex(input: string): string {
  return bytesToHex(sha256(new TextEncoder().encode(input)));
}

/** SHA-256 of raw bytes → hex digest */
export function sha256Bytes(input: Uint8Array): string {
  return bytesToHex(sha256(input));
}

/** SHA-256 of hex-encoded bytes → hex digest */
export function sha256OfHex(hex: string): string {
  return bytesToHex(sha256(hexToBytes(hex)));
}

/** Verify that preimage (hex) hashes to the given sha256 digest (hex) */
export function verifySha256(preimageHex: string, digestHex: string): boolean {
  try {
    return sha256OfHex(preimageHex) === digestHex.toLowerCase();
  } catch {
    return false;
  }
}

/** Cryptographically secure random bytes (browser) */
export function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(out);
  } else {
    // Fallback: NOT secure — only for environments without WebCrypto
    for (let i = 0; i < n; i++) out[i] = Math.floor(Math.random() * 256);
  }
  return out;
}

/** Random hex string of `n` bytes (2n chars) */
export function randomHex(n: number): string {
  return bytesToHex(randomBytes(n));
}

/** HMAC-SHA256 using noble */
import { hmac } from '@noble/hashes/hmac';
export function hmacSha256(key: Uint8Array, message: Uint8Array): Uint8Array {
  return hmac(sha256, key, message);
}

/** Constant-time equality */
export function constantTimeEqual(a: string, b: string): boolean {
  const ab = new TextEncoder().encode(a);
  const bb = new TextEncoder().encode(b);
  if (ab.length !== bb.length) return false;
  let diff = 0;
  for (let i = 0; i < ab.length; i++) diff |= ab[i] ^ bb[i];
  return diff === 0;
}